import { WixPatternsError, isWixPatternsError } from './WixPatternsError';
import { Environment } from './environment';
import type { MonitorLogger } from './MonitorLogger';

type CaptureExceptionOptions = { tags?: Record<string, unknown> };

export interface ErrorMonitor {
  captureException(exception: unknown): string;
}

export interface InternalErrorMonitor {
  captureException(
    exception: unknown,
    options?: CaptureExceptionOptions,
  ): string;
}

export type MonitoredEvent =
  | 'initial-load'
  | 'initial-fetch-data'
  | 'initial-full-render'
  | 'e2e';

export class WixPatternsMonitor {
  private _internalErrorMonitor: InternalErrorMonitor;
  private _errorMonitor: ErrorMonitor;
  private _internalFedops: MonitorLogger | undefined;
  private _reports: Record<string, boolean> = {};
  private _environment: Environment;
  private _csid: string;

  constructor({
    internalErrorMonitor,
    errorMonitor,
    internalFedops,
    environment,
    csid,
  }: {
    internalErrorMonitor: InternalErrorMonitor;
    errorMonitor: ErrorMonitor;
    internalFedops: MonitorLogger | undefined;
    environment: Environment;
    csid: string;
  }) {
    this._internalErrorMonitor = internalErrorMonitor;
    this._errorMonitor = errorMonitor;
    this._internalFedops = internalFedops;
    this._environment = environment;
    this._csid = csid;
  }

  /**
   * This method is used to report errors that occur within the application. It takes in an error object with the following properties:
   * @param errorCode A WixPatternsErrorCode enum value representing the error that occurred.
   * @param exception The exception that was thrown.
   * @param extraData (optional) Additional data related to the error.
   * @param propagate (optional) Propagates report to vertical error monitor.
   * @default propagate true
   */
  reportError({
    error,
    propagate = true,
    category,
  }: {
    error: unknown;
    propagate?: boolean;
    category?: string;
  }) {
    const options: {
      tags?: Record<string, string | boolean | number | undefined | null>;
    } = { tags: { ...this.extraInteractionParams } };

    if (category) {
      options.tags!.category = category;
    }

    this._internalErrorMonitor.captureException(error, options);

    if (this._errorMonitor && propagate) {
      const originalException = isWixPatternsError(error)
        ? (error as WixPatternsError).originalException
        : error;
      this._errorMonitor.captureException(originalException);
    }
  }

  get extraInteractionParams() {
    return {
      cairoVersion: this._environment.uiLibVersion,
      componentId: this._environment.componentId,
      csid: this._csid,
    };
  }

  interactionStarted(interactionName: string) {
    this._internalFedops?.interactionStarted(interactionName, {
      customParams: this.extraInteractionParams,
    });
  }

  interactionEnded(interactionName: string) {
    this._internalFedops?.interactionEnded(interactionName, {
      customParams: this.extraInteractionParams,
    });
  }

  reportInteractionStart(
    componentName: string,
    event: MonitoredEvent,
    customParams?: Record<string, string | boolean>,
  ) {
    const interaction = `${componentName.toLowerCase()}-${event}`;
    const reportKey = `${interaction}-start`;

    if (!this._reports[reportKey]) {
      this._reports[reportKey] = true;
      this._internalFedops?.interactionStarted(interaction, {
        customParams: {
          ...this.extraInteractionParams,
          ...customParams,
        },
      });
    }
  }

  reportInteractionEnd(
    componentName: string,
    event: MonitoredEvent,
    customParams?: Record<string, string | boolean>,
  ) {
    const interaction = `${componentName.toLowerCase()}-${event}`;
    const reportKey = `${interaction}-end`;

    if (!this._reports[reportKey]) {
      this._reports[reportKey] = true;
      this._internalFedops?.interactionEnded(interaction, {
        customParams: {
          ...this.extraInteractionParams,
          ...customParams,
        },
      });
    }
  }

  reportErrorThrownOncePerFlow() {
    this._internalFedops?.reportErrorThrownOncePerFlow();
  }
}
